import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useJsApiLoader } from "@react-google-maps/api";

/** Keep in sync with map components that rely on Places (Autocomplete, etc.). */
export const GOOGLE_MAPS_LIBRARIES: "places"[] = ["places"];

/** Same script id as the former `GoogleMap` loader so cached scripts still match. */
const GOOGLE_MAP_SCRIPT_ID = "google-map-script";

export type MapsLoaderContextValue = {
  isLoaded: boolean;
  loadError: Error | undefined;
};

const MapsLoaderContext = createContext<MapsLoaderContextValue | null>(null);

/**
 * Loads the Google Maps JavaScript API once for the app. Wrap the tree that renders maps
 * (e.g. in `App.tsx`).
 */
export function MapsLoaderProvider({ children }: { children: ReactNode }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAP_SCRIPT_ID,
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const value = useMemo(
    () => ({ isLoaded, loadError }),
    [isLoaded, loadError],
  );

  return (
    <MapsLoaderContext.Provider value={value}>
      {children}
    </MapsLoaderContext.Provider>
  );
}

/**
 * Read Maps JS API load state. Must be called from a React component (or hook) under
 * {@link MapsLoaderProvider}.
 */
export function useMapLoadingStates(): MapsLoaderContextValue {
  const data = useContext(MapsLoaderContext);
  if (data == null) {
    throw new Error(
      "useMapLoadingStates must be used within MapsLoaderProvider",
    );
  }
  return data;
}

/** Alias for {@link useMapLoadingStates} — same hook; call only from components. */
export const getMapLoadingStates = useMapLoadingStates;
